import { useEffect, useId, useRef, useState } from 'react';
import { Camera, Loader2, RotateCw, Upload } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  CHECK_IMAGE_ACCEPT,
  CheckImageError,
  prepareCheckImage,
  type QuarterTurns,
  UNSUPPORTED_CHECK_IMAGE_MESSAGE,
} from '@/lib/payments/checkImage';
import { CHECK_PHOTO_HINT, isFrontSide } from '@/lib/payments/payabliCheckCapture';

/**
 * Where a photo of one side of the check comes from: a file, or Payabli's
 * camera. Shown over the scanner when staff press Front or Back in it.
 *
 * The file picker opens from the button's own click — a user gesture. Opening
 * it straight from the iframe's message is not one, and browsers refuse it.
 *
 * A chosen photo is previewed before it goes to the scanner, so staff can turn
 * a check photographed on its side (nothing in the image says which way up it
 * is). Cancel and the camera stay available while a photo is being prepared;
 * a result that arrives after staff moved on is dropped.
 */
export function CheckCaptureChooser({
  side,
  cameraAvailable,
  onPhoto,
  onCamera,
  onCancel,
}: {
  side: unknown;
  cameraAvailable: boolean;
  /** The photo, ready for the scanner (a 1280×640 JPEG data URL). */
  onPhoto: (dataUrl: string) => void;
  onCamera: () => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLButtonElement>(null);
  const [preparing, setPreparing] = useState(false);
  /** A HEIC photo is being converted, which takes a few seconds. */
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The prepared photo awaiting "Use this photo", and how far it is turned. */
  const [preview, setPreview] = useState<{
    file: File;
    turns: QuarterTurns;
    dataUrl: string;
  } | null>(null);
  /** Bumped on every new request and on leaving; a stale result is ignored. */
  const requestRef = useRef(0);
  const title = isFrontSide(side) ? 'Front of check' : 'Back of check';
  const titleId = useId();

  useEffect(() => {
    uploadRef.current?.focus();
  }, []);

  async function prepare(file: File, turns: QuarterTurns) {
    const request = ++requestRef.current;
    setPreparing(true);
    setError(null);
    try {
      const dataUrl = await prepareCheckImage(file, {
        quarterTurns: turns,
        onConverting: () => {
          if (request === requestRef.current) setConverting(true);
        },
      });
      if (request !== requestRef.current) return;
      setPreview({ file, turns, dataUrl });
    } catch (err) {
      if (request !== requestRef.current) return;
      setError(err instanceof CheckImageError ? err.message : UNSUPPORTED_CHECK_IMAGE_MESSAGE);
    } finally {
      if (request === requestRef.current) {
        setPreparing(false);
        setConverting(false);
      }
    }
  }

  function handleFile(file: File | undefined) {
    // Cleared so choosing the same file again still fires a change.
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setPreview(null);
    void prepare(file, 0);
  }

  /** Leave the chooser: anything still being prepared is dropped. */
  function leave(then: () => void) {
    requestRef.current += 1;
    then();
  }

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-testid="check-capture-chooser"
      className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background p-4"
    >
      <div className="w-full max-w-sm space-y-3 text-center">
        <h3 id={titleId} className="text-base font-semibold">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">{CHECK_PHOTO_HINT}</p>
        <input
          ref={inputRef}
          type="file"
          accept={CHECK_IMAGE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label={`Photo of the ${isFrontSide(side) ? 'front' : 'back'} of the check`}
          data-testid="check-capture-file"
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        {preview ? (
          <img
            src={preview.dataUrl}
            alt={`Preview of the ${isFrontSide(side) ? 'front' : 'back'} of the check`}
            className="w-full rounded-md border"
            data-testid="check-capture-preview"
          />
        ) : null}
        <div className="flex flex-col gap-2">
          {preview ? (
            <>
              <Button
                type="button"
                disabled={preparing}
                onClick={() => leave(() => onPhoto(preview.dataUrl))}
              >
                Use this photo
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={preparing}
                onClick={() =>
                  void prepare(preview.file, ((preview.turns + 1) % 4) as QuarterTurns)
                }
              >
                {preparing ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="mr-1.5 h-4 w-4" />
                )}
                Rotate
              </Button>
            </>
          ) : null}
          <Button
            ref={uploadRef}
            variant={preview ? 'ghost' : 'default'}
            type="button"
            disabled={preparing}
            onClick={() => inputRef.current?.click()}
          >
            {preparing && !preview ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-4 w-4" />
            )}
            {preview ? 'Choose another photo' : 'Upload a photo'}
          </Button>
          {cameraAvailable && !preview ? (
            <Button type="button" variant="outline" onClick={() => leave(onCamera)}>
              <Camera className="mr-1.5 h-4 w-4" />
              Use camera
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => leave(onCancel)}>
            Cancel
          </Button>
        </div>
        {converting ? (
          <p
            role="status"
            className="text-sm text-muted-foreground"
            data-testid="check-capture-converting"
          >
            Converting photo…
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive" data-testid="check-capture-error">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
